"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import {cn} from "@isudio/utils";

/* Radix primitives exported for callers that expect the old API */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

/* Overlay component (kept, but DialogContent will render it conditionally) */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-40 bg-black/80 pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    onWheel={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    onTouchMove={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName || "DialogOverlay";

/* DialogContent props extend Radix's plus our small additions */
type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  /** whether to render the built-in overlay (default true). Set false when stacking dialogs. */
  showOverlay?: boolean;
  /** if provided, the dialog will render a custom close button that calls this function.
   * If omitted, DialogPrimitive.Close is rendered (normal Radix close). */
  onClose?: () => void;
  /** hide the default close button (when you want no close button at all) */
  hideDefaultClose?: boolean;
  zIndex?: number;
  isDimmed?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, showOverlay = true, onClose, hideDefaultClose = false, zIndex, isDimmed = false, ...props }, ref) => {
  const [isSafari, setIsSafari] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const ua = navigator.userAgent.toLowerCase();
      // True Safari contains 'safari' but excludes 'chrome', 'crios', 'chromium', and 'android'
      const isTrueSafari = ua.includes("safari") && !ua.includes("chrome") && !ua.includes("crawlers") && !ua.includes("android");
      setIsSafari(isTrueSafari);
    }
  }, []);

  return (
    <DialogPortal>
      {showOverlay && <DialogOverlay />}
      <DialogPrimitive.Content
        ref={ref}
        style={{ zIndex }} // <CHANGE> Apply z-index via inline style
        className={cn(
          `
          fixed inset-0 m-auto
          
          rounded-sm
          md:rounded-md
  
          gap-4 border-0 outline-none 
          shadow-lg duration-200
        
          data-[state=open]:animate-fade-in-from-opacity 
          data-[state=open]:zoom-in-95 
          data-[state=closed]:animate-fade-out-to-opacity 
          data-[state=closed]:zoom-out-95 
          `,
          className
        )}
        data-dialog-content
        {...props}
      >
        {isDimmed && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-bluegray-darker/80 rounded-lg z-10"
          />
        )}
        {children}

        {/* If caller passes onClose -> render a button that calls it
            Otherwise, render the Radix Close (so other consumers keep normal behavior) */}
        {/*{onClose ? (*/}
        {/*  <button*/}
        {/*    type="button"*/}
        {/*    onClick={onClose}*/}
        {/*    className="absolute right-4 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"*/}
        {/*    aria-label="Close"*/}
        {/*  >*/}
        {/*    <i className="fa-duotone fa-light fa-square-xmark text-2xl hover:text-red-500 cursor-pointer" />*/}
        {/*    <span className="sr-only">Close</span>*/}
        {/*  </button>*/}
        {/*) : !hideDefaultClose ? (*/}
        {/*  <DialogPrimitive.Close*/}
        {/*    className="absolute right-4 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"*/}
        {/*    aria-label="Close"*/}
        {/*  >*/}
        {/*    <i className="fa-duotone fa-light fa-square-xmark text-2xl hover:text-red-500 cursor-pointer" />*/}
        {/*    <span className="sr-only">Close</span>*/}
        {/*  </DialogPrimitive.Close>*/}
        {/*) : null}*/}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName || "DialogContent";

/* Small layout helpers — kept as before for compatibility */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName || "DialogTitle";

const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName || "DialogDescription";

/* Export exactly the same shape as your earlier file so imports remain unchanged */
export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
